import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useLocation, useParams, useSearchParams } from 'react-router'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { signingTokenParamSchema } from '@lib/validators/params'
import { signingKeys } from '@/hooks/queryKeys'
import { useSigningSession } from '@/hooks/useSigningSession'
import { useQuestionSession } from '@/hooks/useQuestionSession'
import { downloadBlob } from '@/helpers/download'
import logger, { toErrorMessage } from '@/helpers/logger'
import { signService } from '@/services/SignService'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PacketViewer } from '@/signing/PacketViewer'
import { AdoptSignature, type Adoption } from '@/signing/AdoptSignature'
import { ConsentAndAdopt } from '@/signing/ConsentAndAdopt'
import { NextRequiredFooter, goToFirstField } from '@/signing/NextRequiredFooter'
import { FinishHint } from '@/signing/FinishHint'
import { PaperCopyRequest } from '@/signing/PaperCopyRequest'
import { QuestionSidebar } from '@/signing/QuestionSidebar'
import {
  DocumentToc,
  SignShell,
  SignStatus,
  TestBadge,
  requestErrorMessage,
} from '@/signing/SignChrome'
import { hasUnansweredThreads } from '@/signing/question-threads'
import { remainingRequired } from '@/signing/field-fill'
import { useForcedTheme } from '@/theme/ThemeProvider'
import { cn } from '@/lib/utils'
import { signedOnLabel } from '@/signing/backfill-signed-dates'
import type { FieldDto, FieldValueDto } from '@lib/dto/envelope.dto'
import { embedChromeSchema, embedEndingSchema, embedThemeSchema } from '@lib/dto/embed-message.dto'
import { embedHost, useEmbedHost, useFramedDialogs } from '@/signing/embed'
import type { SigningSessionDto } from '@lib/dto/signing-session.dto'
import { declineSigningInputSchema, requestPaperCopyInputSchema } from '@lib/input/signing.input'
import { CONSENT_VERSION } from '@lib/constants/envelope'
import { useBrandLook } from '@/signing/brand-look'
import { DeclineConsentDialog } from '@/signing/DeclineConsentDialog'
import { useSigningDraft } from '@/signing/useSigningDraft'


export function SignPage() {
  const location = useLocation()
  const embedded = location.pathname.startsWith('/embed/')
  const [searchParams] = useSearchParams()
  useForcedTheme(embedded ? embedThemeSchema.parse(searchParams.get('theme') ?? undefined) : 'light')
  const { token: rawToken } = useParams()
  const parsedToken = signingTokenParamSchema.safeParse(rawToken)
  const token = parsedToken.success ? parsedToken.data : undefined
  const queryClient = useQueryClient()
  const sessionQuery = useSigningSession(token)
  const [formError, setFormError] = React.useState<string | null>(null)
  const [scrollToDocumentId, setScrollToDocumentId] = React.useState<string | undefined>()
  const [adopted, setAdopted] = React.useState<Adoption | null>(null)
  // Opens on arrival; the signer may close it to read or decline first.
  const [adoptOpen, setAdoptOpen] = React.useState(true)
  const [declineOpen, setDeclineOpen] = React.useState(false)
  const [declineConsentOpen, setDeclineConsentOpen] = React.useState(false)
  const [declineReason, setDeclineReason] = React.useState('')
  const [finishWarned, setFinishWarned] = React.useState(false)

  const session = sessionQuery.data
  useBrandLook(session?.brand)
  const reportDraftError = React.useCallback((error: Error) => {
    logger.warn('Signing draft did not save', { error: toErrorMessage(error) })
    setFormError(requestErrorMessage(error))
  }, [])
  const showSession = React.useCallback(
    (fresh: SigningSessionDto) => {
      if (token) queryClient.setQueryData(signingKeys.session(token), fresh)
    },
    [queryClient, token],
  )
  const { values, setValues, mergedFromOtherTab, flushDraft, fieldsForComplete } = useSigningDraft(
    token,
    session,
    reportDraftError,
    showSession,
  )
  const reportEmbedError = useEmbedHost(embedded, session)
  useFramedDialogs(embedded)
  const ending = embedEndingSchema.parse(searchParams.get('ending') ?? undefined)
  const chrome = embedded ? embedChromeSchema.parse(searchParams.get('chrome') ?? undefined) : 'full'
  useSignerRedirect(embedded, session)

  const questionsOn =
    Boolean(session?.questionsEnabled) && session?.gate === 'ready'
  const question = useQuestionSession({
    token,
    source: 'sign',
    enabled: questionsOn,
    canAsk: Boolean(
      session &&
        session.questionRole === 'asker' &&
        session.envelope.status === 'pending',
    ),
  })

  const loadDocument = React.useCallback(
    (documentId: string) => {
      if (!token) {
        return Promise.reject(new Error('Missing signing token'))
      }
      return signService.getDocument(token, documentId)
    },
    [token],
  )
  const loadAsset = React.useCallback(
    (documentId: string, assetId: string) => {
      if (!token) {
        return Promise.reject(new Error('Missing signing token'))
      }
      return signService.getAsset(token, documentId, assetId)
    },
    [token],
  )

  // Carries the signature adopted with consent, if any, so it's in place before
  // the session turns ready and the adopt dialog would otherwise open.
  const consentMutation = useMutation<SigningSessionDto, Error, Adoption | null>({
    mutationFn: () => {
      if (!token) {
        throw new Error('Missing signing token')
      }
      return signService.acceptConsent(token, { version: CONSENT_VERSION })
    },
    onError: reportEmbedError,
    onSuccess: (data, adoption) => {
      if (adoption) {
        setAdopted(adoption)
      }
      if (token) {
        queryClient.setQueryData(signingKeys.session(token), data)
      }
    },
  })

  const completeMutation = useMutation({
    mutationFn: async () => {
      if (!token) {
        throw new Error('Missing signing session')
      }
      // Finish sends the values on screen. A draft that failed to save must not block that.
      try {
        await flushDraft()
      } catch (error) {
        logger.warn('Signing draft did not save before finish', { error: toErrorMessage(error) })
      }
      return signService.complete(token, { fields: fieldsForComplete() })
    },
    onSuccess: (data) => {
      setFormError(null)
      if (token) {
        queryClient.setQueryData(signingKeys.session(token), data)
      }
    },
    onError: (error) => {
      reportEmbedError(error)
      setFormError(requestErrorMessage(error))
    },
  })

  const unsignedDownload = useMutation({
    mutationFn: async () => {
      if (!token || !session) {
        throw new Error('Missing signing session')
      }
      const blob = await signService.getUnsignedPacket(token)
      downloadBlob(blob, packetDownloadName(session.envelope.title, 'unsigned'))
    },
    onError: (error) => {
      reportEmbedError(error)
      setFormError(requestErrorMessage(error))
    },
  })

  const signedDownload = useMutation({
    mutationFn: async () => {
      if (!token || !session) {
        throw new Error('Missing signing session')
      }
      const blob = await signService.getSignedPacket(token)
      downloadBlob(blob, packetDownloadName(session.envelope.title, 'signed'))
    },
    onError: (error) => {
      reportEmbedError(error)
      setFormError(requestErrorMessage(error))
    },
  })

  const certificateDownload = useMutation({
    mutationFn: async () => {
      if (!token || !session) {
        throw new Error('Missing signing session')
      }
      const blob = await signService.getCertificate(token)
      downloadBlob(blob, packetDownloadName(session.envelope.title, 'certificate'))
    },
    onError: (error) => {
      reportEmbedError(error)
      setFormError(requestErrorMessage(error))
    },
  })

  const declineMutation = useMutation({
    mutationFn: async (reason: string) => {
      if (!token) {
        throw new Error('Missing signing token')
      }
      const parsed = declineSigningInputSchema.safeParse({ reason })
      if (!parsed.success) {
        throw new Error(parsed.error.issues[0]?.message ?? 'Enter a reason')
      }
      try {
        await flushDraft()
      } catch (error) {
        logger.warn('Signing draft did not save before decline', { error: toErrorMessage(error) })
      }
      return signService.decline(token, parsed.data)
    },
    onSuccess: (data) => {
      setFormError(null)
      if (token) {
        queryClient.setQueryData(signingKeys.session(token), data)
      }
    },
    onError: (error) => {
      reportEmbedError(error)
      setFormError(requestErrorMessage(error))
    },
  })

  const paperCopyMutation = useMutation({
    mutationFn: async (note: string) => {
      if (!token) {
        throw new Error('Missing signing token')
      }
      const parsed = requestPaperCopyInputSchema.safeParse(note.length > 0 ? { note } : {})
      if (!parsed.success) {
        throw new Error(parsed.error.issues[0]?.message ?? 'Check the note')
      }
      return signService.requestPaperCopy(token, parsed.data)
    },
    onSuccess: (data) => {
      if (!token) {
        return
      }
      // Only the request's own field: the rest of the page (a draft being
      // signed, say) carries on as it was.
      queryClient.setQueryData<SigningSessionDto>(signingKeys.session(token), (current) =>
        current
          ? { ...current, signer: { ...current.signer, paperCopyRequestedAt: data.signer.paperCopyRequestedAt ?? null } }
          : data,
      )
    },
    onError: (error) => {
      reportEmbedError(error)
    },
  })

  function updateValue(field: FieldDto, value: FieldValueDto): void {
    setValues((current) => {
      const next = { ...current, [field.id]: value }
      if (field.type === 'radio' && field.group && session) {
        for (const other of session.fields) {
          if (other.id !== field.id && other.group === field.group) {
            next[other.id] = ''
          }
        }
      }
      return next
    })
  }

  function applyStamp(field: FieldDto): void {
    if (!adopted) {
      return
    }
    const stamp = field.type === 'initials' ? adopted.initials : adopted.signature
    setValues((current) => {
      const next = { ...current, [field.id]: stamp }
      if (!session || field.type !== 'signature') {
        return next
      }
      const signedOn = signedOnLabel(session.timeZone)
      for (const other of session.fields) {
        if (
          other.type === 'date_signed' &&
          other.signerId === field.signerId &&
          other.documentId === field.documentId &&
          other.page === field.page
        ) {
          next[other.id] = signedOn
        }
      }
      return next
    })
  }

  if (!token) {
    return <SignStatus title="Invalid link" body="This signing link is not valid." />
  }

  if (sessionQuery.isLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (sessionQuery.isError || !session) {
    return (
      <SignStatus
        title="Link not found"
        body={
          sessionQuery.error
            ? requestErrorMessage(sessionQuery.error)
            : 'This signing link is invalid or has expired.'
        }
      />
    )
  }

  if (embedded) {
    if (!embedHost(session)) {
      return (
        <SignStatus
          brand={session.brand.displayName}
          logoSrc={session.brand.logoSrc}
          accentColor={session.brand.accentColor}
          title="Embedding blocked"
          body="This parent origin is not allowed to embed PixelSign."
        />
      )
    }
  }

  /** The paper-copy request, for a signer who has consented, once the API takes requests. */
  function paperCopyRequest(stillSigning: boolean): React.ReactNode {
    if (!session || session.paperCopyFeeMicros === undefined || !session.signer.consentAcceptedAt) {
      return null
    }
    return (
      <PaperCopyRequest
        organization={session.brand.displayName}
        feeMicros={session.paperCopyFeeMicros}
        requestedAt={session.signer.paperCopyRequestedAt ?? null}
        stillSigning={stillSigning}
        requesting={paperCopyMutation.isPending}
        error={paperCopyMutation.isError ? requestErrorMessage(paperCopyMutation.error) : null}
        onRequest={(note) => paperCopyMutation.mutate(note)}
      />
    )
  }

  if (
    embedded &&
    ending === 'host' &&
    (session.gate === 'signed' ||
      (session.gate === 'unavailable' && session.signer.status === 'declined'))
  ) {
    // The host has the message and shows its own ending; nothing here competes with it.
    return (
      <div role="status" className="bg-background">
        <span className="sr-only">
          {session.gate === 'signed' ? 'Signed' : 'Declined'}
        </span>
      </div>
    )
  }

  if (session.gate === 'unavailable') {
    // The signer who declined is told what that means, not shown their own reason back.
    const declinedHere = session.signer.status === 'declined'
    return (
      <SignStatus
        brand={session.brand.displayName}
        logoSrc={session.brand.logoSrc}
        accentColor={session.brand.accentColor}
        chrome={chrome}
        title={declinedHere ? 'You declined to sign' : 'This request is closed'}
        body={
          declinedHere
            ? `${session.brand.displayName} has been told. If you change your mind, contact them for a new request.`
            : session.unavailableReason
        }
      />
    )
  }

  if (session.gate === 'waiting') {
    return (
      <SignStatus
        brand={session.brand.displayName}
        logoSrc={session.brand.logoSrc}
        accentColor={session.brand.accentColor}
        chrome={chrome}
        title="Not your turn yet"
        body={`${session.envelope.title} will be ready after earlier signers finish.`}
      />
    )
  }

  if (session.gate === 'signed') {
    const completed = session.envelope.status === 'completed'
    return (
      <SignShell
        brand={session.brand.displayName}
        logoSrc={session.brand.logoSrc}
        accentColor={session.brand.accentColor}
        title={session.envelope.title}
        test={session.envelope.mode === 'test'}
        chrome={chrome}
      >
        <Card className="mx-auto max-w-md">
          <CardHeader>
            <div className="mb-2">
              <CheckCircle2 className="h-5 w-5 text-foreground" />
            </div>
            <CardTitle>You’re done</CardTitle>
            <CardDescription>
              {completed
                ? `${session.envelope.title} is complete. Download your signed copy and the certificate of completion to keep them.`
                : `${session.envelope.title} is signed on your side. Other signers still need to finish before the signed PDF is ready.`}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {formError ? (
              <p className="w-full text-sm text-destructive">{formError}</p>
            ) : null}
            {completed ? (
              <Button
                disabled={signedDownload.isPending}
                onClick={() => {
                  setFormError(null)
                  signedDownload.mutate()
                }}
              >
                {signedDownload.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Download signed PDF
              </Button>
            ) : null}
            {completed ? (
              <Button
                type="button"
                variant="outline"
                disabled={certificateDownload.isPending}
                onClick={() => {
                  setFormError(null)
                  certificateDownload.mutate()
                }}
              >
                {certificateDownload.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Download certificate of completion
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              disabled={unsignedDownload.isPending}
              onClick={() => {
                setFormError(null)
                unsignedDownload.mutate()
              }}
            >
              {unsignedDownload.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              Download unsigned PDF
            </Button>
            <div className="w-full">{paperCopyRequest(false)}</div>
          </CardContent>
        </Card>
      </SignShell>
    )
  }

  if (session.gate === 'consent') {
    const consentError = consentMutation.isError
      ? requestErrorMessage(consentMutation.error)
      : null
    const declineConsent = () => {
      declineMutation.reset()
      setDeclineConsentOpen(true)
    }
    return (
      <SignShell
        brand={session.brand.displayName}
        logoSrc={session.brand.logoSrc}
        accentColor={session.brand.accentColor}
        title={session.envelope.title}
        test={session.envelope.mode === 'test'}
        chrome={chrome}
      >
        {adoptsAtConsent(session) ? (
          <ConsentAndAdopt
            signerName={session.signer.name}
            envelopeTitle={session.envelope.title}
            consentText={session.consentText}
            needsInitials={session.inkFields.initials}
            agreeing={consentMutation.isPending}
            declining={declineMutation.isPending}
            error={consentError}
            onAgree={(adoption) => consentMutation.mutate(adoption)}
            onDecline={declineConsent}
          />
        ) : (
          <Card className="mx-auto max-w-xl">
            <CardHeader>
              <CardTitle>Review and consent</CardTitle>
              <CardDescription>
                Hi {session.signer.name}. Before you can view {session.envelope.title},
                please agree to sign electronically.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="max-h-64 overflow-y-auto rounded-md border border-border bg-muted/40 p-4 text-sm leading-6 text-foreground whitespace-pre-wrap">
                {session.consentText}
              </div>
              {consentError ? (
                <p className="text-sm text-destructive">{consentError}</p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={consentMutation.isPending}
                  onClick={() => consentMutation.mutate(null)}
                >
                  {consentMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  I agree — continue
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={declineMutation.isPending}
                  onClick={declineConsent}
                >
                  I don’t agree
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        <DeclineConsentDialog
          open={declineConsentOpen}
          onOpenChange={setDeclineConsentOpen}
          organization={session.brand.displayName}
          declining={declineMutation.isPending}
          error={declineMutation.isError ? requestErrorMessage(declineMutation.error) : null}
          onDecline={(reason) => declineMutation.mutate(reason)}
        />
      </SignShell>
    )
  }

  const ownFields = session.fields.filter(
    (field) => field.signerId === session.signer.id,
  )
  const needsInk = ownFields.some(
    (field) => field.type === 'signature' || field.type === 'initials',
  )
  const needsAdopt = session.canSign && needsInk && adopted == null
  const showAdopt = needsAdopt && adoptOpen
  const remaining = remainingRequired(ownFields, values)
  const canFinish = session.canSign && !needsAdopt && remaining.length === 0
  const unanswered = hasUnansweredThreads(session.threads)
  const questionRole = session.questionRole
  const actionError = formError ?? question.error
  const testEnvelope = session.envelope.mode === 'test'
  const hostChrome = chrome === 'host'

  const downloadButton = (
    <Button
      type="button"
      variant="outline"
      disabled={unsignedDownload.isPending}
      onClick={() => {
        setFormError(null)
        unsignedDownload.mutate()
      }}
    >
      {unsignedDownload.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : null}
      Download unsigned PDF
    </Button>
  )
  const finishButton = session.canSign ? (
    <Button
      className={
        canFinish && !completeMutation.isPending
          ? 'sign-finish-ready'
          : undefined
      }
      disabled={!canFinish || completeMutation.isPending}
      aria-describedby={canFinish ? undefined : 'finish-hint'}
      onClick={() => {
        if (unanswered && !finishWarned) {
          setFinishWarned(true)
          setFormError(
            'You still have unanswered questions. Click Finish signing again to complete anyway.',
          )
          return
        }
        setFormError(null)
        completeMutation.mutate()
      }}
    >
      {completeMutation.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : null}
      {unanswered && finishWarned ? 'Finish anyway' : 'Finish signing'}
    </Button>
  ) : null
  const finishHint = session.canSign && !canFinish ? (
    <FinishHint
      remainingCount={remaining.length}
      onShowField={() => goToFirstField(remaining)}
      onAdopt={() => setAdoptOpen(true)}
    />
  ) : null
  const declineButton = session.canSign ? (
    <Button
      type="button"
      variant="outline"
      disabled={declineMutation.isPending}
      onClick={() => {
        setFormError(null)
        setDeclineOpen(true)
      }}
    >
      Decline to sign
    </Button>
  ) : null
  const errorLine = (
    <>
      {mergedFromOtherTab ? (
        <p role="status" className="text-sm text-muted-foreground">
          Changes you made in another tab or window were brought in.
        </p>
      ) : null}
      {actionError ? <p className="text-sm text-destructive">{actionError}</p> : null}
    </>
  )
  const questionPanel =
    questionsOn && questionRole ? (
      <QuestionSidebar
        role={questionRole}
        threads={session.threads}
        selectedThreadId={question.selectedThreadId}
        draft={question.draft}
        composer={question.composer}
        pending={question.pending}
        onComposerChange={question.onComposerChange}
        onAsk={question.onAsk}
        onReply={question.onReply}
        onCancelDraft={question.onCancelDraft}
        onSelectThread={question.onSelectThread}
        viewers={session.viewers}
      />
    ) : null
  const viewer = (
    <PacketViewer
      documents={session.documents}
      fields={session.fields}
      values={values}
      viewerSignerId={session.signer.id}
      signedSignerIds={session.signedSignerIds}
      onChange={updateValue}
      onClickSign={(field) => {
        if (!adopted) {
          setAdoptOpen(true)
          return
        }
        applyStamp(field)
      }}
      loadDocument={loadDocument}
      loadAsset={loadAsset}
      questions={
        questionsOn && questionRole
          ? {
              threads: session.threads,
              draft: question.draft,
              selectedThreadId: question.selectedThreadId,
              onSelectThread: question.onSelectThread,
              ...(question.onAskPin ? { onAskPin: question.onAskPin } : {}),
            }
          : undefined
      }
      watermark={hostChrome && testEnvelope}
      titled={!hostChrome || session.documents.length > 1}
      {...(scrollToDocumentId ? { scrollToDocumentId } : {})}
    />
  )

  // The host shows the title, so its embed is the documents and, after them,
  // the actions: a frame sized to its content can't pin a bar to the screen.
  const hostLayout = (
    <div
      className={cn(
        'mx-auto grid max-w-6xl gap-6',
        questionPanel && 'lg:grid-cols-[minmax(0,1fr)_18rem]',
      )}
    >
      <div className="min-w-0 space-y-4">
        {viewer}
        <Card className="space-y-3 p-card">
          {errorLine}
          <div className="flex flex-wrap items-center gap-3">
            {testEnvelope ? <TestBadge /> : null}
            <div className="ml-auto flex flex-wrap gap-2">
              {downloadButton}
              {declineButton}
              {finishButton}
            </div>
          </div>
          {finishHint}
          {paperCopyRequest(session.canSign)}
        </Card>
      </div>
      {questionPanel ? <div className="h-fit lg:sticky lg:top-6">{questionPanel}</div> : null}
    </div>
  )
  const multipleDocuments = session.documents.length > 1
  const fullLayout = (
    <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
      {viewer}
      <div className="h-fit space-y-6 lg:sticky lg:top-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {multipleDocuments ? 'Documents' : session.signer.name}
            </CardTitle>
            {multipleDocuments ? (
              <CardDescription>{session.signer.name}</CardDescription>
            ) : null}
          </CardHeader>
          <CardContent className="space-y-4">
            {multipleDocuments ? (
              <DocumentToc
                documents={session.documents}
                onSelect={setScrollToDocumentId}
              />
            ) : null}
            {errorLine}
            <div className="flex flex-wrap gap-2">
              {downloadButton}
              {finishButton}
              {declineButton}
            </div>
            {finishHint}
            {paperCopyRequest(session.canSign)}
          </CardContent>
        </Card>
        {questionPanel}
      </div>
    </div>
  )

  return (
    <SignShell
      brand={session.brand.displayName}
      logoSrc={session.brand.logoSrc}
      accentColor={session.brand.accentColor}
      title={session.envelope.title}
      // With the host's chrome, the watermark and the action bar's badge say it.
      test={testEnvelope && !hostChrome}
      chrome={chrome}
    >
      {needsAdopt ? (
        <AdoptSignature
          open={adoptOpen}
          onOpenChange={setAdoptOpen}
          name={session.signer.name}
          email={session.signer.email}
          needsInitials={ownFields.some((field) => field.type === 'initials')}
          onAdopt={(next) => {
            setAdopted(next)
            setValues((current) =>
              clearStampValues(ownFields, current),
            )
          }}
        />
      ) : null}
      {session.signer.status === 'signed' ? (
        <p className="mx-auto mb-4 max-w-6xl text-sm text-muted-foreground">
          You’re signed. You can still ask and reply until this request closes.
        </p>
      ) : null}
      {hostChrome ? hostLayout : fullLayout}
      <NextRequiredFooter
        remaining={remaining}
        // A frame its host sizes to the content has no screen bottom to pin
        // the bar to; the line under Finish does the job there.
        hidden={showAdopt || !session.canSign || hostChrome}
      />
      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline to sign?</DialogTitle>
            <DialogDescription>
              Tell the sender why you are declining. This closes the request for
              everyone.
            </DialogDescription>
          </DialogHeader>
          <Field label="Reason" htmlFor="decline-reason">
            <Textarea
              id="decline-reason"
              value={declineReason}
              onChange={(event) => setDeclineReason(event.target.value)}
              placeholder="Reason"
              rows={4}
              maxLength={2000}
            />
          </Field>
          {declineMutation.isError ? (
            <p className="text-sm text-destructive">
              {requestErrorMessage(declineMutation.error)}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeclineOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={declineMutation.isPending || declineReason.trim().length === 0}
              onClick={() => declineMutation.mutate(declineReason.trim())}
            >
              Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SignShell>
  )
}

/**
 * A signer the sender delivers (signed in to the sender's app) adopts their
 * signature on the consent step, when they have anything to sign now.
 */
function adoptsAtConsent(session: SigningSessionDto): boolean {
  return (
    session.signer.delivery === 'caller' &&
    (session.inkFields.signature || session.inkFields.initials)
  )
}

function packetDownloadName(
  title: string,
  kind: 'unsigned' | 'signed' | 'certificate',
): string {
  const base = title.replace(/[^\w.\- ]+/g, '_').trim() || 'envelope'
  return `${base}-${kind}.pdf`
}

function clearStampValues(
  fields: readonly FieldDto[],
  current: Record<string, FieldValueDto>,
): Record<string, FieldValueDto> {
  const next = { ...current }
  for (const field of fields) {
    if (field.type === 'signature' || field.type === 'initials') {
      next[field.id] = { kind: 'type', data: '' }
    }
    if (field.type === 'date_signed') {
      next[field.id] = ''
    }
  }
  return next
}


function useSignerRedirect(
  embedded: boolean,
  session: SigningSessionDto | undefined,
): void {
  React.useEffect(() => {
    if (embedded || !session || session.gate !== 'signed') {
      return
    }
    const redirect = session.signer.redirectUrl
    if (!redirect) {
      return
    }
    const handle = window.setTimeout(() => {
      window.location.assign(redirect)
    }, 1200)
    return () => window.clearTimeout(handle)
  }, [embedded, session])
}
